import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { FaSearch, FaShoppingCart } from "react-icons/fa";
import Header from "../Components/Header";
import LoadingState from "../Components/States/LoadingState";
import Footer from "../Components/Footer";
import LabCatalogCard, { FeaturedLabCard, CategoryPromoCard } from "../Components/LabCatalogCard";
import { getStaffTestPackages, getLabTests, getScans } from "../api/catalog.api";
import { getCart, addToCart } from "../api/cart.api";
import { getStaffId, STORAGE_KEYS } from "../utils/storage";
import { toast } from "../Components/Toast";
import {
  LAB_TAB,
  mapPackage,
  mapTest,
  mapScan,
  pickFeaturedPackage,
  groupPackagesByCategory,
  filterCatalog,
} from "../utils/labTests";

const mergeDiagnosticIds = (existing, incoming) => {
  const set = new Set(Array.isArray(existing) ? existing : []);
  (incoming || []).forEach((id) => { if (id) set.add(id); });
  return [...set];
};

const LabTestsDiscoverPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const staffId = getStaffId();

  const [activeTab, setActiveTab] = useState(() => {
    if (searchParams.get("tab") === "packages") return LAB_TAB.packages;
    if (location.state?.tab === LAB_TAB.packages) return LAB_TAB.packages;
    return LAB_TAB.testsScans;
  });
  const [packages, setPackages] = useState([]);
  const [tests, setTests] = useState([]);
  const [scans, setScans] = useState([]);
  const [cartIds, setCartIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fastingOnly, setFastingOnly] = useState(false);
  const [homeOnly, setHomeOnly] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState(null);

  useEffect(() => {
    if (!staffId) { setLoading(false); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const [pkgRes, testRes, scanRes, cartRes] = await Promise.all([
          getStaffTestPackages(staffId).catch(() => ({ data: {} })),
          getLabTests(staffId).catch(() => ({ data: {} })),
          getScans(staffId).catch(() => ({ data: {} })),
          getCart(staffId).catch(() => ({ data: { items: [] } })),
        ]);
        if (cancelled) return;
        const rawPkgs = pkgRes.data?.myPackages || [];
        const featured = pickFeaturedPackage(rawPkgs.map((p) => mapPackage(p)));
        setPackages(rawPkgs.map((p) => {
          const mapped = mapPackage(p, { isFeatured: featured?.id === (p.packageId || p._id) });
          return mapped;
        }));
        const testData = testRes.data?.data || [];
        setTests(testData.map(mapTest));
        const scanData = scanRes.data?.data || [];
        setScans(scanData.map(mapScan));
        setCartIds((cartRes.data?.items || []).map((i) => i.itemId));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [staffId]);

  const testsScansItems = useMemo(() => [...tests, ...scans], [tests, scans]);

  const filteredPackages = useMemo(() => {
    let items = packages;
    if (categoryFilter) items = items.filter((p) => (p.category || "Other") === categoryFilter);
    return filterCatalog(items, { search, fastingOnly, homeOnly });
  }, [packages, search, fastingOnly, homeOnly, categoryFilter]);

  const filteredTestsScans = useMemo(
    () => filterCatalog(testsScansItems, { search, fastingOnly, homeOnly }),
    [testsScansItems, search, fastingOnly, homeOnly]
  );

  const featuredPackage = useMemo(() => filteredPackages.find((p) => p.isFeatured) || filteredPackages[0], [filteredPackages]);
  const standardPackages = useMemo(
    () => filteredPackages.filter((p) => p.id !== featuredPackage?.id),
    [filteredPackages, featuredPackage]
  );
  const categoryGroups = useMemo(() => groupPackagesByCategory(packages), [packages]);

  const handleAddToCart = async (item) => {
    if (!staffId) return;
    try {
      const diagnosticId = item.diagnosticIds?.[0] || null;
      const res = await addToCart(staffId, { itemId: item.id, action: "inc", diagnosticId });
      if (res.status === 200) {
        setCartIds((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
        toast.success(`${item.title} added to cart.`);
        if (item.diagnosticIds?.length) {
          const existing = JSON.parse(localStorage.getItem(STORAGE_KEYS.cartDiagnosticIds) || "[]");
          localStorage.setItem(
            STORAGE_KEYS.cartDiagnosticIds,
            JSON.stringify(mergeDiagnosticIds(existing, item.diagnosticIds))
          );
        }
      } else {
        toast.error("Failed to add item to cart.");
      }
    } catch (err) {
      console.error("Add to cart failed:", err);
      toast.error("Error adding item to cart.");
    }
  };

  const handleCardClick = (item) => {
    navigate("/lab-tests/book", { state: { item, itemType: item.itemType } });
  };

  const toggle = (key) => {
    if (key === "fasting") setFastingOnly((v) => !v);
    if (key === "home") setHomeOnly((v) => !v);
  };

  const gridItems = activeTab === LAB_TAB.packages ? standardPackages : filteredTestsScans;

  return (
    <div className="bg-background min-h-screen flex flex-col">
      <Header />
      <div className="flex flex-col min-h-screen pb-16 lg:pb-0 flex-1">
        <main className="flex-1 max-w-7xl mx-auto w-full px-3 sm:px-6 py-4 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-6">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-on-primary-fixed-var">Discover Lab Tests</h1>
              <p className="text-sm sm:text-base text-on-surface-variant mt-1">Corporate wellness packages and diagnostic tests at partner labs.</p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/cart")}
              className="inline-flex items-center gap-2 self-start px-3 sm:px-4 py-2 rounded-lg border border-surface-container bg-surface-lowest text-primary text-sm font-semibold"
            >
              <FaShoppingCart /> Cart {cartIds.length > 0 && `(${cartIds.length})`}
            </button>
          </div>

          <div className="inline-flex bg-surface-low rounded-full p-1 border border-surface-container mb-4">
            {[
              { label: "Tests / Scans", value: LAB_TAB.testsScans },
              { label: "Packages", value: LAB_TAB.packages },
            ].map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => { setActiveTab(tab.value); setCategoryFilter(null); }}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                  activeTab === tab.value
                    ? "bg-primary-fixed text-on-primary-fixed-var"
                    : "text-on-surface-variant hover:text-on-primary-fixed-var"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="relative max-w-xl mb-4">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant" />
            <input
              type="text"
              placeholder="Search for tests, packages, or symptoms..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-surface-container bg-surface-lowest focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="mb-6 -mx-3 sm:mx-0">
            <div className="flex gap-2 overflow-x-auto px-3 sm:px-0 pb-1">
              {[
                { key: "fasting", label: "Fasting Required", on: fastingOnly },
                { key: "home", label: "Home Collection", on: homeOnly },
              ].map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => toggle(chip.key)}
                  className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                    chip.on
                      ? "bg-primary text-white border-primary"
                      : "bg-surface-lowest text-on-surface-variant border-surface-container"
                  }`}
                >
                  {chip.label}
                </button>
              ))}
              {activeTab === LAB_TAB.packages && categoryGroups.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setCategoryFilter(null)}
                    className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                      !categoryFilter
                        ? "bg-primary text-white border-primary"
                        : "bg-surface-lowest text-on-surface-variant border-surface-container"
                    }`}
                  >
                    All
                  </button>
                  {categoryGroups.map((g) => (
                    <button
                      key={g.name}
                      type="button"
                      onClick={() => setCategoryFilter(categoryFilter === g.name ? null : g.name)}
                      className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                        categoryFilter === g.name
                          ? "bg-primary text-white border-primary"
                          : "bg-surface-lowest text-on-surface-variant border-surface-container"
                      }`}
                    >
                      {g.name}
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>

          {loading && <LoadingState branded message="Loading catalog…" />}

          {!loading && activeTab === LAB_TAB.packages && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredPackage && (
                <FeaturedLabCard
                  item={featuredPackage}
                  inCart={cartIds.includes(featuredPackage.id)}
                  onAddToCart={() => handleAddToCart(featuredPackage)}
                  onCardClick={() => handleCardClick(featuredPackage)}
                  hideCart
                />
              )}
              {categoryGroups.slice(0, 1).map((g) => (
                <CategoryPromoCard
                  key={g.name}
                  name={g.name}
                  count={g.count}
                  onClick={() => setCategoryFilter(categoryFilter === g.name ? null : g.name)}
                />
              ))}
              {gridItems.map((item) => (
                <LabCatalogCard
                  key={item.id}
                  item={item}
                  inCart={cartIds.includes(item.id)}
                  onAddToCart={() => handleAddToCart(item)}
                  onCardClick={() => handleCardClick(item)}
                  hideCart
                />
              ))}
              {!featuredPackage && !gridItems.length && (
                <p className="col-span-full text-center text-on-surface-variant py-8">No packages match your filters.</p>
              )}
            </div>
          )}

          {!loading && activeTab === LAB_TAB.testsScans && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {gridItems.map((item) => (
                <LabCatalogCard
                  key={`${item.itemType}-${item.id}`}
                  item={item}
                  inCart={cartIds.includes(item.id)}
                  onAddToCart={() => handleAddToCart(item)}
                  onCardClick={() => handleCardClick(item)}
                />
              ))}
              {!gridItems.length && (
                <p className="col-span-full text-center text-on-surface-variant py-8">No tests or scans match your filters.</p>
              )}
            </div>
          )}
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default LabTestsDiscoverPage;
